import { useMemo } from 'react';
import { Contract, isArray, isNumber, isObject, isString } from 'validata';
import { useBoolFlag, useObjectFlag } from '../flags';
import { AppliedFilter, AppliedFilterType } from '../entity-search';

interface DataExportConfigFlagValue {
  enabledViews: {
    entityType: string;
  }[];
  defaultExportLimit: number;
}

export interface DataExportSettings {
  limit: number;
  entityType: string;
}

export type DataExportConfig = {
  isEnabled: false;
} | {
  isEnabled: true;
  settings: DataExportSettings;
}

export interface DataExportOverrides {
  getFilterString?: (filters: AppliedFilter<AppliedFilterType>[]) => string;
  getSort?: () => string;
}

const dataExportConfigFlagContract: Contract<DataExportConfigFlagValue> = {
  defaultExportLimit: isNumber(),
  enabledViews: isArray(
    isObject({
      entityType: isString(),
    }),
  ),
};

const checkDataExportConfigFlag = isObject(dataExportConfigFlagContract);

export const useDataExportConfig = (entityType: string): DataExportConfig => {
  const isEnabled = useBoolFlag('portals-data-exporter-micro-portal-feature');
  const configFlag = useObjectFlag('portals-data-exporter-micro-portal-supported-views', checkDataExportConfigFlag);

  return useMemo(() => {
    if (!isEnabled || !configFlag) return { isEnabled: false };

    const config = configFlag.enabledViews.find((view) => view.entityType === entityType);
    if (!config) return { isEnabled: false };

    return {
      isEnabled: true,
      settings: {
        limit: configFlag.defaultExportLimit,
        entityType: config.entityType,
      },
    };
  }, [isEnabled, configFlag, entityType]);
};
